import { useCallback } from "react";

import { isIos, isMobileNative } from "~/utils/helpers";

import { LONG_SKIP_SECONDS, SHORT_SKIP_SECONDS } from "../media/constants";
import { useMediaPlayerContext } from "../media/context";
import { AudioControl } from "./AudioControl";
import { AudioTimeline } from "./AudioTimeline";
import { ExpandedVolumeControl, VolumeControl } from "../media/VolumeControl";
import { PlaybackRateControl } from "../media/PlaybackRateControl";

import type { ReactNode } from "react";

export interface AudioControlsProps {
  isExpanded?: boolean;
  isDesktopLayout?: boolean;
  onAirplayControlClick?: () => void;
  onReplayControlClick?: () => void;
  onExpandedChange?: () => void;
}

export function AudioControls({
  isExpanded,
  isDesktopLayout,
  onAirplayControlClick,
  onReplayControlClick,
  onExpandedChange,
}: AudioControlsProps) {
  const {
    airplaying,
    canAirplay,
    captionsEnabled,
    currentTime,
    ended,
    playbackRate,
    paused,
    volume,
    seek,
    play,
    pause,
    setCaptions,
    setPlaybackRate,
    setVolume,
  } = useMediaPlayerContext();

  const handleAirplayControlClick = useCallback(() => {
    onAirplayControlClick?.();
  }, [onAirplayControlClick]);

  const handleGoBackClick = useCallback(() => {
    seek(currentTime - SHORT_SKIP_SECONDS);
  }, [currentTime, seek]);

  const handleGoForwardClick = useCallback(() => {
    seek(currentTime + LONG_SKIP_SECONDS);
  }, [currentTime, seek]);

  const handleReplayClick = useCallback(() => {
    seek(0);
    onReplayControlClick?.();
  }, [onReplayControlClick, seek]);

  const handlePlayPauseClick = useCallback(() => {
    if (paused) {
      play();
    } else {
      pause();
    }
  }, [pause, paused, play]);

  const handleCaptionsClick = useCallback(() => {
    setCaptions(!captionsEnabled);
  }, [captionsEnabled, setCaptions]);

  const handlePlaybackRateChange = useCallback(
    (playbackRate: number) => {
      setPlaybackRate(playbackRate);
    },
    [setPlaybackRate],
  );

  const handleVolumeChange = useCallback(
    (volume: number) => {
      setVolume(volume);
    },
    [setVolume],
  );

  const handleExpandCollapseClick = useCallback(() => {
    onExpandedChange?.();
  }, [onExpandedChange]);

  const renderIfExpanded = useCallback(
    (node: ReactNode) => {
      return isExpanded ? node : null;
    },
    [isExpanded],
  );

  const showPopoverVolumeControl =
    !isMobileNative() && !isIos() && !isDesktopLayout;

  return (
    <div className="AudioControls">
      <AudioTimeline />

      <div className="AudioControls__playback">
        {renderIfExpanded(
          <AudioControl
            appearance="secondary"
            title="Go back"
            type="go-backward"
            onClick={handleGoBackClick}
          />,
        )}

        {!ended && (
          <AudioControl
            appearance="primary"
            isActive={paused}
            title="Play"
            type="play-pause"
            onClick={handlePlayPauseClick}
          />
        )}

        {ended && (
          <AudioControl
            appearance="primary"
            title="Replay"
            type="replay"
            onClick={handleReplayClick}
          />
        )}

        {renderIfExpanded(
          <AudioControl
            appearance="secondary"
            title="Go forward"
            type="go-forward"
            onClick={handleGoForwardClick}
          />,
        )}
      </div>

      <div className="AudioControls__utils">
        {renderIfExpanded(
          <AudioControl
            appearance="tertiary"
            isActive={captionsEnabled}
            type="closed-captioning"
            onClick={handleCaptionsClick}
          />,
        )}

        {renderIfExpanded(
          <PlaybackRateControl
            className="AudioControl AudioControl--tertiary AudioControl--has-text"
            popoverPlacement="top"
            value={playbackRate}
            onChange={handlePlaybackRateChange}
          />,
        )}

        {showPopoverVolumeControl &&
          renderIfExpanded(
            <VolumeControl
              classes="AudioControl"
              popoverPlacement="top"
              value={volume}
              onChange={handleVolumeChange}
            />,
          )}

        {canAirplay &&
          renderIfExpanded(
            <AudioControl
              appearance="tertiary"
              type="airplay"
              isActive={airplaying}
              onClick={handleAirplayControlClick}
            />,
          )}

        {Boolean(onExpandedChange) && (
          <AudioControl
            appearance="tertiary"
            isActive={!isExpanded}
            type="expand"
            onClick={handleExpandCollapseClick}
          />
        )}
      </div>
      {isDesktopLayout && (
        <ExpandedVolumeControl
          classes="AudioControl--expanded-volume"
          buttonClasses="AudioControl"
          value={volume}
          onChange={handleVolumeChange}
        />
      )}
    </div>
  );
}
